var productName = 'scheduler';import DomHelper from '../../Core/helper/DomHelper.js';
import InstancePlugin from '../../Core/mixin/InstancePlugin.js';
import GridFeatureManager from '../../Grid/feature/GridFeatureManager.js';
import Delayable from '../../Core/mixin/Delayable.js';
/**
 * @module Scheduler/feature/ScheduleContext
 */
/**
 * Allow visually selecting a schedule "cell" by clicking
 *
 * This feature is **disabled** by default
 *
 * ```javascript
 * const scheduler = new Scheduler({
 *     features : {
 *         scheduleContext : true // `false` by default, set to `true` to enable the feature
 *     }
 * });
 * ```
 *
 * The contextual details are available in the {@link #property-context} property.
 * 
 * **Note that the context is cleared upon change of {@link Scheduler.view.Scheduler#property-viewPreset}
 * such as when zooming in or out.**
 *
 * @extends Core/mixin/InstancePlugin
 * @inlineexample Scheduler/feature/ScheduleContext.js
 * @classtype scheduleContext
 * @feature
 */
export default class ScheduleContext extends InstancePlugin.mixin(Delayable) {
    static get $name() {
        return 'ScheduleContext';
    }
    static delayable = {
        syncContextElement : 'raf'
    }
    /**
     * The contextual information about which cell was clicked on and highlighted.
     *
     * When the {@link Scheduler.view.Scheduler#property-viewPreset} is changed (such as when zooming)
     * the context is cleared and the highlight is removed.
     *
     * @member {Object} context
     * @property {Scheduler.view.TimelineBase} context.source The owning Scheduler
     * @property {Date} context.date Date at mouse position
     * @property {Scheduler.model.TimeSpan} context.tick A record which encapsulates the time axis tick clicked on.
     * @property {Number} context.tickIndex The index of the time axis tick clicked on.
     * @property {Date} context.tickStartDate The start date of the current time axis tick
     * @property {Date} context.tickEndDate The end date of the current time axis tick
     * @property {Grid.row.Row} context.row Clicked row (in horizontal mode only)
     * @property {Number} context.index Index of clicked resource
     * @property {Scheduler.model.ResourceModel} context.resourceRecord Resource record
     * @property {MouseEvent} context.event Browser event
     */
    construct(client, config) {
        // required to work
        client.useBackgroundCanvas = true;
        client.on({
            scheduleclick           : 'onScheduleClick',
            schedulecontextmenu     : 'onScheduleClick',
            datachange              : 'syncContextElement',
            timeaxisviewmodelupdate : 'onTimeAxisViewModelUpdate',
            presetchange            : 'clearContext',
            thisObj                 : this
        });
        client.rowManager.on({
            rowheight : 'syncContextElement',
            thisObj   : this
        });
        super.construct(client, config);
    }
    get element() {
        return this._element || (this._element = DomHelper.createElement({
            parent    : this.client.backgroundCanvas,
            className : 'b-schedule-selected-tick'
        }));
    }
    onScheduleClick(context) {
        this.context = context;
        this.syncContextElement();
    }
    onTimeAxisViewModelUpdate({ source : timeAxisViewModel }) {
        // Just a mutation of existing tick details, sync the element
        if (timeAxisViewModel.timeAxis.includes(this.context?.tick)) {
            this.syncContextElement();
        }
        // The tick has gone, we have moved to a new ViewPreset, so clear the context.
        else {
            this.clearContext();
        }
    }
    clearContext() {
        if (this.context) {
            this.context = null;
            this.element.style.display = 'none';
        }
    }
    syncContextElement() {
        if (this.context) {
            const
                row = this.client.getRowFor(this.context.resourceRecord),
                {
                    client,
                    element
                } = this,
                {
                    style
                } = element;
            if (row) {
                const
                    {
                        tickStartDate,
                        tickEndDate,
                        resourceRecord
                    } = this.context,
                    // get the position clicked based on dates
                    renderData = client.currentOrientation.getTimeSpanRenderData({
                        startDate   : tickStartDate,
                        endDate     : tickEndDate,
                        startDateMS : tickStartDate.getTime(),
                        endDateMS   : tickEndDate.getTime()
                    }, resourceRecord);
                let top, height;
                if (client.isVertical) {
                    top = renderData.top;
                    height = renderData.height;
                }
                else {
                    top = row.top;
                    height = row.height;
                }
                // Move to current cell
                style.display = '';
                style.width = `${renderData.width}px`;
                style.height = `${height}px`;
                DomHelper.setTranslateXY(element, renderData.left, top);
            }
            // No row for resource might mean it's scrolled out of view or filtered out
            // so just hide so that the next valid sync can restore it to visibility
            else {
                style.display = 'none';
            }
        }
    }
}
ScheduleContext.featureClass = 'b-scheduler-context';
ScheduleContext._$name = 'ScheduleContext'; GridFeatureManager.registerFeature(ScheduleContext, false, ['Scheduler']);
